/**
 * adminStore — Admin 페이지 간 공유 상태 (Zustand)
 *
 * Admin 섹션의 여러 데이터 페이지(Users, AIGenerations, Payments, CreditTx 등)가
 * 공통으로 사용하는 cross-page 상태를 관리한다.
 *
 * - **selectedUserId**: AdminUsers에서 유저를 선택하면 다른 페이지
 *   (예: CreditTransactions)에서 해당 유저로 자동 필터링.
 * - **filters**: 페이지네이션, 정렬, 검색 등 공통 필터.
 *   각 Admin 페이지의 로컬 state(탭 선택, 모달 열림 등)와는 분리한다.
 *
 * @example
 *   const { selectedUserId, setSelectedUserId } = useAdminStore()
 *
 *   // 유저 테이블에서 행 클릭 시
 *   setSelectedUserId(user.id)
 *
 *   // 다른 페이지에서 해당 유저 데이터만 조회
 *   if (selectedUserId) fetchByUserId(selectedUserId)
 */

import { create } from 'zustand'

/** Admin 데이터 테이블의 공통 필터 인터페이스 */
interface AdminFilters {
  /** 텍스트 검색어 (이름, 이메일 등) */
  search?: string
  /** 현재 페이지 번호 (1부터 시작) */
  page: number
  /** 페이지당 행 수 */
  pageSize: number
  /** 정렬 기준 컬럼명 */
  sortBy: string
  /** 정렬 방향 */
  sortOrder: 'asc' | 'desc'
}

/** 필터 초기값 — resetFilters()에서도 사용 */
const DEFAULT_FILTERS: AdminFilters = {
  page: 1,
  pageSize: 20,
  sortBy: 'createdAt',
  sortOrder: 'desc',
}

interface AdminState {
  /* ── 선택된 유저 ── */
  /** 현재 선택된 유저 ID. null이면 전체 조회 */
  selectedUserId: string | null
  setSelectedUserId: (id: string | null) => void

  /* ── 공통 필터 ── */
  filters: AdminFilters
  /** 일부 필터만 변경 (나머지는 유지) */
  setFilters: (filters: Partial<AdminFilters>) => void
  /** 필터를 초기값으로 리셋 */
  resetFilters: () => void
}

export const useAdminStore = create<AdminState>((set) => ({
  selectedUserId: null,
  setSelectedUserId: (id) => set({ selectedUserId: id }),

  filters: { ...DEFAULT_FILTERS },
  setFilters: (partial) => set((s) => ({ filters: { ...s.filters, ...partial } })),
  resetFilters: () => set({ filters: { ...DEFAULT_FILTERS } }),
}))

export type { AdminFilters }
